import { useEffect } from "react";

/**
 * Loads a CSS file lazily by creating a link element and appending it to the document head.
 * This function uses the `preload` attribute to load the stylesheet without blocking rendering,
 * and then switches it to `stylesheet` once the file is loaded.
 * This is useful for performance optimization, especially for large stylesheets or when you want to defer loading until the browser is idle.
 * 
 * @param href The URL of the CSS file to be loaded lazily.
 * @returns {void}
 */
export const loadCSS = (href: string, rel: string = 'preload'): void => {
    console.log(`Loading stylesheet: ${href} with rel=${rel}`);
    const link = document.createElement('link');
    link.rel = rel;
    link.type = 'text/css';
    link.href = href;
    link.as = 'style';
    if (rel === 'preload') {
        link.onload = () => {
            link.rel = 'stylesheet';
            // this may load the css file twice when using preload, but this is a common pattern
        };
    }
    document.head.appendChild(link);
    console.log(`Stylesheet loaded: ${href}`);
};

/**
 * 
 * Custom hook to lazily load stylesheets in a React component.
 * 
 * This hook uses the `useEffect` hook to ensure that the styles are loaded only once when the component mounts.
 * 
 * @returns null
 */
const useLazyLoadStyles = () => {
    useEffect(() => {
        if (typeof window !== "undefined") {
            const href = "/icons.css";
            if (document.querySelector(`link[href='${href}']`)) return; // Prevent duplicate loading
            console.log("Lazy Load stylesheet hook called");
            const load = () => loadCSS(href, 'stylesheet');
            if ("requestIdleCallback" in window) {
                console.log('Lazy Load stylesheet using requestIdleCallback');
                (window as any).requestIdleCallback(load);
            } else if (typeof (window as any).requestAnimationFrame === "function") {
                console.log('Lazy Load stylesheet using requestAnimationFrame');
                (window as any).requestAnimationFrame(load);
            } else {
                console.log('Lazy Load stylesheet using setTimeout as fallback flow');
                setTimeout(load, 200);
            }
        }
    }, []);
    return null;
};

export default useLazyLoadStyles;